import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { authApi } from '../services/api'

// ── Auth Store ─────────────────────────────────────────────────
export const useAuthStore = create(
    persist(
        (set, get) => ({
            user:  null,
            token: null,
            login: async (data) => {
                const res = await authApi.login(data)
                const { token, user } = res.data
                localStorage.setItem('auth_token', token)
                set({ user, token })
                return res.data
            },
            logout:          () => { localStorage.removeItem('auth_token'); set({ user: null, token: null }) },
            setAuth:         (user, token) => set({ user, token }),
            isLoggedIn:      ()            => !!get().token,
            isAuthenticated: ()            => !!get().token,
            isSuperAdmin:    ()            => get().user?.role === 'superadmin',
            isAdmin:         ()            => ['admin','superadmin'].includes(get().user?.role),
        }),
        { name: 'signage-auth' }
    )
)

// ── Echo / WebSocket Store ─────────────────────────────────────
export const useEchoStore = create((set) => ({
    echo:       null,
    connected:  false,
    init:       () => {},
    setEcho:    (echo) => set({ echo, connected: true }),
    disconnect: ()     => set({ echo: null, connected: false }),
}))

// ── Toast Store ────────────────────────────────────────────────
export const useToastStore = create((set, get) => ({
    toasts: [],
    addToast: (toast) => {
        const id = Date.now()
        set(s => ({ toasts: [...s.toasts, { ...toast, id }] }))
        setTimeout(() => get().removeToast(id), toast.duration ?? 4000)
    },
    removeToast: (id) => set(s => ({ toasts: s.toasts.filter(t => t.id !== id) })),
}))

// ── Alert Store (pro AlertsPage + Layout notifikace) ──────────
export const useAlertStore = create((set, get) => ({
    // Toast notifikace
    toasts: [],
    addToast: (toast) => {
        const id = Date.now()
        set(s => ({ toasts: [...s.toasts, { ...toast, id }] }))
        setTimeout(() => get().removeToast(id), toast.duration ?? 4000)
    },
    removeToast: (id) => set(s => ({ toasts: s.toasts.filter(t => t.id !== id) })),

    // Signage alerty
    alerts:      [],
    unseenCount: 0,
    loadAlerts: async () => {
        try {
            const { alertsApi } = await import('../services/api')
            const res  = await alertsApi.list()
            const data = res.data?.data ?? res.data ?? []
            set({ alerts: data, unseenCount: data.filter(a => !a.seen_at).length })
        } catch (e) {
            console.warn('loadAlerts failed:', e)
            set({ alerts: [] })
        }
    },
    markSeen: async (id) => {
        try {
            const { alertsApi } = await import('../services/api')
            await alertsApi.markSeen(id)
            set(s => ({
                alerts:      s.alerts.map(a => a.id === id ? { ...a, seen_at: new Date() } : a),
                unseenCount: Math.max(0, s.unseenCount - 1),
            }))
        } catch (e) { console.warn('markSeen failed:', e) }
    },
    markAllSeen: async () => {
        try {
            const { alertsApi } = await import('../services/api')
            await alertsApi.markAllSeen()
            set(s => ({
                alerts:      s.alerts.map(a => ({ ...a, seen_at: new Date() })),
                unseenCount: 0,
            }))
        } catch (e) { console.warn('markAllSeen failed:', e) }
    },
}))

// ── Screen Store ───────────────────────────────────────────────
export const useScreenStore = create((set) => ({
    screens:        [],
    selectedScreen: null,
    lastScreenshot: {},
    setScreens:     (screens) => set({ screens }),
    selectScreen:   (screen)  => set({ selectedScreen: screen }),
    setScreenshot:  (screenId, data) => set(s => ({
        lastScreenshot: { ...s.lastScreenshot, [screenId]: data }
    })),
}))

// ── Stats Store ────────────────────────────────────────────────
export const useStatsStore = create((set) => ({
    stats:    null,
    setStats: (stats) => set({ stats }),
}))

// ── Sensor Store ───────────────────────────────────────────────
export const useSensorStore = create((set) => ({
    sensors:    [],
    setSensors: (sensors) => set({ sensors }),
}))
